import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { apiGet } from './api';
import { ensureSocket } from './socket';
import { PermissionProvider } from './permissions';
import SignIn from './SignIn';
import Admin from './Admin/Admin';
import Reseller from './Reseller/Reseller';
import Agency from './Agency/Agency';
import { BrowserRouter as Router } from 'react-router-dom';
import './index.css';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Role now comes from the MongoDB backend (checks admins → resellers →
  // agencies for the caller's uid). Returns 'superadmin'|'admin'|'reseller'|
  // 'agency' or null, plus — for an admin — the permission map a Super Admin
  // has set for them (null for the other roles, and every key true for a
  // superadmin).
  const fetchUserRole = async () => {
    try {
      const { role, permissions } = await apiGet('/api/account/me');
      return { role: role || null, permissions: permissions || null };
    } catch (error) {
      throw new Error("A database error occurred while verifying your role.");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setAuthError(null);
      if (authUser) {
        try {
          const { role, permissions } = await fetchUserRole(authUser.uid);
          if (role) {
            setUser({ ...authUser, role, permissions });
          } else {
            const errorMsg = "Your account was not found in any user role collection (Admin, Reseller, Agency). Please contact support.";
            console.error(errorMsg, "UID:", authUser.uid);
            setAuthError(errorMsg);
            signOut(auth);
          }
        } catch (error) {
          console.error("Critical error during role verification:", error);
          setAuthError(error.message);
          signOut(auth);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // A Super Admin changing an admin's permissions emits account:updated to that
  // admin. Re-read them live, so the panel doesn't sit on a menu the server has
  // already stopped honouring until the next reload.
  const signedInUid = user?.uid;
  const signedInRole = user?.role;
  useEffect(() => {
    if (!signedInUid || (signedInRole !== 'admin' && signedInRole !== 'superadmin')) return undefined;
    let alive = true;
    let sock = null;
    const refresh = async () => {
      try {
        const { role, permissions } = await fetchUserRole();
        if (alive && role) setUser(prev => (prev ? { ...prev, role, permissions } : prev));
      } catch (_) { /* keep what we have; the routes enforce it anyway */ }
    };
    ensureSocket().then((s) => {
      if (!alive || !s) return;
      sock = s;
      s.on('account:updated', refresh);
    });
    return () => { alive = false; if (sock) sock.off('account:updated', refresh); };
  }, [signedInUid, signedInRole]);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', flexDirection: 'column', gap: 16,
        background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 100%)'
      }}>
        <div style={{
          width: 48, height: 48,
          border: '3px solid rgba(255,255,255,0.1)',
          borderLeftColor: '#ff4d6d',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, margin: 0 }}>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  
  const getHomeComponent = () => {
    if (!user) {
      return <SignIn authError={authError} />;
    }
    switch (user.role) {
      case 'superadmin':
      case 'admin':
        return (
          <PermissionProvider role={user.role} permissions={user.permissions}>
            <Admin userRole={user.role} />
          </PermissionProvider>
        );
      case 'reseller':
        return <Reseller />;
      case 'agency':
        return <Agency />;
      default:
        
        return <SignIn authError="User role is undetermined. Please sign in again." />;
    }
  }

  return (
    <Router>
        {getHomeComponent()}
    </Router>
  );
};

export default App;